import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { CVMHttpService } from '@core/http/cvm.http.service';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { parseError } from '@state/errors.parser';
import { FileHandlerService } from '@core/services/file-handler.service';
import * as cvmActions from './cvm.actions';

@Injectable()
export class CVMEffects {
  private readonly actions$ = inject(Actions);
  private readonly cvmHttp = inject(CVMHttpService);
  private readonly fileHandlerService = inject(FileHandlerService);
  private readonly snackService = inject(StackedMessagesService);

  searchCandidateVerifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.searchCandidateVerifications),
      switchMap(({ opts }) => {
        const { all, count, ...payload } = opts;

        return this.cvmHttp.searchCandidateVerifications(payload).pipe(
          map(candidateVerifications => cvmActions.searchCandidateVerificationsSuccess({ candidateVerifications })),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.searchCandidateVerifications.type);
            this.snackService.showError({ message: 'MANAGER.ERROR_SEARCHING_CANDIDATE_VERIFICATIONS' });
            return of(cvmActions.searchCandidateVerificationsError({ errorMessage }));
          })
        );
      })
    )
  );

  getCandidateVerificationDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.getCandidateVerificationDetails),
      switchMap(({ candidateVerificationId }) => {
        return this.cvmHttp.getCandidateVerificationDetails(candidateVerificationId).pipe(
          map(candidateVerification => cvmActions.getCandidateVerificationDetailsSuccess({ candidateVerification })),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.getCandidateVerificationDetails.type);
            this.snackService.showError({ message: 'CVM.ERROR_FETCHING_CANDIDATE_VERIFICATION_DETAILS' });
            return of(cvmActions.getCandidateVerificationDetailsError({ errorMessage }));
          })
        );
      })
    )
  );

  getCandidateVerificationHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.getCandidateVerificationHistory),
      switchMap(({ candidateVerificationId }) => {
        return this.cvmHttp.getCandidateVerificationHistory(candidateVerificationId).pipe(
          map(candidateVerificationHistory =>
            cvmActions.getCandidateVerificationHistorySuccess({ candidateVerificationHistory })
          ),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.getCandidateVerificationHistory.type);
            this.snackService.showError({ message: 'CVM.ERROR_FETCHING_CANDIDATE_VERIFICATION_HISTORY' });
            return of(cvmActions.getCandidateVerificationHistoryError({ errorMessage }));
          })
        );
      })
    )
  );

  addNewCandidateVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.addNewCandidateVerification),
      switchMap(({ opts }) => {
        return this.cvmHttp.newCandidateVerification(opts).pipe(
          map(({ candidateVerification, smsResult }) =>
            cvmActions.addNewCandidateVerificationSuccess({ candidateVerification, smsResult })
          ),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.addNewCandidateVerification.type);
            this.snackService.showError({ message: 'CVM.ERROR_ADDING_CANDIDATE_VERIFICATION' });
            return of(cvmActions.addNewCandidateVerificationError({ errorMessage }));
          })
        );
      })
    )
  );

  updateCandidateVerificationBasicDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.updateCandidateVerificationBasicDetails),
      switchMap(({ candidateVerificationId, opts }) => {
        return this.cvmHttp.updateCandidateVerificationDetails(candidateVerificationId, opts).pipe(
          switchMap(({ smsResult }) =>
            this.cvmHttp
              .getCandidateVerificationDetails(candidateVerificationId)
              .pipe(
                map(candidateVerification =>
                  cvmActions.updateCandidateVerificationBasicDetailsSuccess({ candidateVerification, smsResult })
                )
              )
          ),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.updateCandidateVerificationBasicDetails.type);
            this.snackService.showError({ message: 'CVM.ERROR_UPDATING_CANDIDATE_VERIFICATION' });
            return of(cvmActions.updateCandidateVerificationBasicDetailsError({ errorMessage }));
          })
        );
      })
    )
  );

  updateCandidateVerificationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.updateCandidateVerificationStatus),
      switchMap(({ candidateVerificationId, opts }) => {
        return this.cvmHttp.updateCandidateVerificationStatus(candidateVerificationId, opts).pipe(
          map(({ candidateVerification }) =>
            cvmActions.updateCandidateVerificationStatusSuccess({ candidateVerification })
          ),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.updateCandidateVerificationStatus.type);
            this.snackService.showError({ message: 'CVM.ERROR_UPDATING_CANDIDATE_VERIFICATION_STATUS' });
            return of(cvmActions.updateCandidateVerificationStatusError({ errorMessage }));
          })
        );
      })
    )
  );

  saveCandidateVerificationResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.saveCandidateVerificationResult),
      switchMap(({ candidateVerificationId, opts }) => {
        return this.cvmHttp.saveCandidateVerificationResult(candidateVerificationId, opts).pipe(
          switchMap(() =>
            this.cvmHttp
              .getCandidateVerificationDetails(candidateVerificationId)
              .pipe(
                map(candidateVerification =>
                  cvmActions.saveCandidateVerificationResultSuccess({ candidateVerification })
                )
              )
          ),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.saveCandidateVerificationResult.type);
            return of(cvmActions.saveCandidateVerificationResultError({ errorMessage }));
          })
        );
      })
    )
  );

  setCandidateVerificationDecision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.setCandidateVerificationDecision),
      switchMap(({ candidateVerificationId, opts }) => {
        return this.cvmHttp.setCandidateVerificationDecision(candidateVerificationId, opts).pipe(
          switchMap(() =>
            this.cvmHttp
              .getCandidateVerificationDetails(candidateVerificationId)
              .pipe(
                map(candidateVerification =>
                  cvmActions.setCandidateVerificationDecisionSuccess({ candidateVerification })
                )
              )
          ),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.setCandidateVerificationDecision.type);
            this.snackService.showError({ message: 'CVM.ERROR_SETTING_CANDIDATE_VERIFICATION_DECISION' });
            return of(
              cvmActions.setCandidateVerificationDecisionError({
                candidateVerificationId,
                opts,
                errorMessage,
              })
            );
          })
        );
      })
    )
  );

  updateCandidateVerificationProcedureDecision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.updateCandidateVerificationProcedureDecision),
      switchMap(({ candidateVerificationId, procedureId, decision }) => {
        return this.cvmHttp
          .updateCandidateVerificationProcedureDecision(candidateVerificationId, procedureId, decision)
          .pipe(
            switchMap(() =>
              this.cvmHttp.getCandidateVerificationDetails(candidateVerificationId).pipe(
                map(candidateVerification =>
                  cvmActions.updateCandidateVerificationProcedureDecisionSuccess({
                    candidateVerificationId,
                    procedureId,
                    decision,
                    candidateVerification,
                  })
                )
              )
            ),
            catchError(error => {
              const errorMessage = parseError(error, cvmActions.updateCandidateVerificationProcedureDecision.type);
              this.snackService.showError({ message: 'CVM.ERROR_UPDATING_CANDIDATE_VERIFICATION_PROCEDURE_DECISION' });
              return of(
                cvmActions.updateCandidateVerificationProcedureDecisionError({
                  candidateVerificationId,
                  procedureId,
                  decision,
                  errorMessage,
                })
              );
            })
          );
      })
    )
  );

  addCandidateVerificationExtraService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.addCandidateVerificationExtraService),
      switchMap(({ candidateVerificationId, serviceType }) => {
        return this.cvmHttp.addCandidateVerificationExtraService(candidateVerificationId, serviceType).pipe(
          switchMap(() =>
            this.cvmHttp.getCandidateVerificationDetails(candidateVerificationId).pipe(
              map(candidateVerification =>
                cvmActions.addCandidateVerificationExtraServiceSuccess({
                  candidateVerificationId,
                  serviceType,
                  candidateVerification,
                })
              )
            )
          ),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.addCandidateVerificationExtraService.type);
            this.snackService.showError({ message: 'CVM.ERROR_ADDING_CANDIDATE_VERIFICATION_EXTRA_SERVICE' });
            return of(
              cvmActions.addCandidateVerificationExtraServiceError({
                candidateVerificationId,
                serviceType,
                errorMessage,
              })
            );
          })
        );
      })
    )
  );

  requestCandidateVerificationSupplementaryInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.requestCandidateVerificationSupplementaryInfo),
      switchMap(({ candidateVerificationId, opts }) => {
        return this.cvmHttp.requestSuplementaryInfo(candidateVerificationId, opts).pipe(
          map(({ candidateVerification, smsResult }) =>
            cvmActions.requestCandidateVerificationSupplementaryInfoSuccess({ candidateVerification, smsResult })
          ),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.requestCandidateVerificationSupplementaryInfo.type);
            this.snackService.showError({ message: 'CVM.ERROR_REQUESTING_CANDIDATE_VERIFICATION_SUPPLEMENTARY_INFO' });
            return of(cvmActions.requestCandidateVerificationSupplementaryInfoError({ errorMessage }));
          })
        );
      })
    )
  );

  regenerateFormLinkAndPin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.regenerateCandidateFormLinkAndPin),
      switchMap(({ candidateVerificationId }) => {
        return this.cvmHttp.regenerateFormLinkAndPin(candidateVerificationId).pipe(
          switchMap(({ smsResult }) =>
            this.cvmHttp
              .getCandidateVerificationDetails(candidateVerificationId)
              .pipe(
                map(candidateVerification =>
                  cvmActions.regenerateCandidateFormLinkAndPinSuccess({ candidateVerification, smsResult })
                )
              )
          ),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.regenerateCandidateFormLinkAndPin.type);
            this.snackService.showError({ message: 'CVM.ERROR_REGENERATING_FORM_LINK_AND_PIN' });
            return of(cvmActions.regenerateCandidateFormLinkAndPinError({ errorMessage }));
          })
        );
      })
    )
  );

  extendCandidateVerificationFormValidity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.extendCandidateVerificationFormValidity),
      switchMap(({ candidateVerificationId, extendByDays }) => {
        return this.cvmHttp.extendCandidateVerificationFormValidity({ candidateVerificationId, extendByDays }).pipe(
          map(({ candidateVerification, smsResult }) =>
            cvmActions.extendCandidateVerificationFormValiditySuccess({ candidateVerification, smsResult })
          ),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.extendCandidateVerificationFormValidity.type);
            this.snackService.showError({ message: 'CVM.ERROR_EXTENDING_CANDIDATE_VERIFICATION_FORM_VALIDITY' });
            return of(cvmActions.extendCandidateVerificationFormValidityError({ errorMessage }));
          })
        );
      })
    )
  );

  deleteCandidateVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.deleteCandidateVerification),
      switchMap(({ candidateVerificationId }) => {
        return this.cvmHttp.deleteCandidateVerification(candidateVerificationId).pipe(
          map(({ deletedCandidateVerificationId }) =>
            cvmActions.deleteCandidateVerificationSuccess({ deletedCandidateVerificationId })
          ),
          catchError(error => {
            const errorMessage = parseError(error, cvmActions.deleteCandidateVerification.type);
            this.snackService.showError({ message: 'CVM.ERROR_DELETING_CANDIDATE_VERIFICATION' });
            return of(cvmActions.deleteCandidateVerificationError({ errorMessage }));
          })
        );
      })
    )
  );

  openCandidateVerificationFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cvmActions.openCandidateVerificationFile),
      mergeMap(({ candidateVerificationId, fileId, previewTargetKey }) => {
        return this.cvmHttp.downloadCandidateFile(candidateVerificationId, fileId).pipe(
          map(response => {
            if (
              this.fileHandlerService.getFile(response, {
                tryOpenOnMobile: true,
                previewTargetKey,
                allowDownloadFallback: true,
              })
            ) {
              return cvmActions.openCandidateVerificationFileSuccess({ fileId });
            }

            return cvmActions.openCandidateVerificationFileError({ fileId, errorMessage: null });
          }),
          catchError(error => {
            if (previewTargetKey) {
              this.fileHandlerService.clearPreparedPreviewTarget(previewTargetKey);
            }
            const errorMessage = parseError(error, cvmActions.openCandidateVerificationFile.type);
            this.snackService.showError({ message: 'COMMON.ERROR.DOWNLOADING_ATTACHMENT' });
            return of(cvmActions.openCandidateVerificationFileError({ fileId, errorMessage }));
          })
        );
      })
    )
  );
}
